import * as React from "react"

import { setToastTranslator } from "@/lib/api"

import { en, type Messages } from "./messages/en"
import { ru } from "./messages/ru"
import { uz } from "./messages/uz"
import { detectLocale, resolveMessage, type Locale } from "./resolve"

const CATALOGS: Record<Locale, Messages> = { en, ru, uz }
const STORAGE_KEY = "ai-agents.locale"

type Translate = (key: string, vars?: Record<string, string | number>) => string

interface LocaleState {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: Translate
}

const LocaleContext = React.createContext<LocaleState | null>(null)

function initialLocale(): Locale {
  if (typeof window === "undefined") return "en" // prerender always passes `locale`
  let stored: string | null = null
  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch {
    /* storage unavailable; detection still works */
  }
  return detectLocale(window.location.pathname, stored, navigator.languages ?? [])
}

export function LocaleProvider({
  locale: forced,
  children,
}: {
  locale?: Locale
  children: React.ReactNode
}) {
  const [chosen, setChosen] = React.useState<Locale>(() => forced ?? initialLocale())
  const locale = forced ?? chosen

  React.useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale
  }, [locale])

  const setLocale = React.useCallback((next: Locale) => {
    setChosen(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* choice simply will not persist */
    }
  }, [])

  const t = React.useCallback<Translate>(
    (key, vars) => resolveMessage(CATALOGS[locale], en, locale, key, vars),
    [locale],
  )

  // Let non-React helpers (toasts, error boundary) localize their labels.
  React.useEffect(() => {
    setToastTranslator((key, fallback) => {
      const resolved = t(key)
      return resolved === key ? fallback : resolved
    })
  }, [t])

  const value = React.useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): Pick<LocaleState, "locale" | "setLocale"> {
  const context = React.useContext(LocaleContext)
  if (!context) throw new Error("useLocale must be used inside LocaleProvider")
  return context
}

export function useT(): Translate {
  const context = React.useContext(LocaleContext)
  if (!context) throw new Error("useT must be used inside LocaleProvider")
  return context.t
}
